import axios from "axios";
import api from "../../../conf/api";

/**
 * Browser-direct S3 multipart uploader.
 *
 * Parts are PUT straight to S3 with short-lived presigned URLs — they never
 * pass through Django, so throughput isn't bounded by gunicorn worker count,
 * the Coolify VM's disk, or the old "upload to the server, then the server
 * re-uploads to S3" double transfer. Django only handles small JSON calls
 * (init / presign / complete / abort / status).
 *
 * Resumable: S3 itself knows which parts landed (ListParts, surfaced by the
 * status endpoint), so a reload, crash or dropped connection re-sends only
 * the missing parts instead of restarting a multi-GB upload. The session id
 * is remembered in localStorage against a fingerprint of the file, so
 * picking the same file again after a refresh picks up where it left off.
 */

// How many parts to keep in flight at once. Each is a full part-sized PUT
// straight to S3, so this is about saturating the uplink without starving
// the rest of the page's requests.
const DEFAULT_CONCURRENCY = 4;

const PART_MAX_RETRIES = 4;
const PART_RETRY_BASE_DELAY_MS = 1000; // 1s, 2s, 4s, 8s

const RESUME_STORAGE_PREFIX = "cv.s3upload.";
// Presigned URLs expire (1h server-side); anything older than this is
// re-presigned rather than retried against a dead URL.
const RESUME_MAX_AGE_MS = 20 * 60 * 60 * 1000; // 20h, under the server's 24h session TTL

/** Stable-enough identity for "is this the same file the user picked before". */
const fingerprint = (file, kind) =>
  `${RESUME_STORAGE_PREFIX}${kind}.${file.name}.${file.size}.${file.lastModified}`;

const readResume = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved?.session_key || Date.now() - (saved.saved_at || 0) > RESUME_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
};

const writeResume = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, saved_at: Date.now() }));
  } catch {
    /* quota/private mode — resume is a nicety, never block the upload for it */
  }
};

const clearResume = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

export class S3Uploader {
  constructor(file, kind = "video") {
    this.file = file;
    this.kind = kind; // "video" | "thumbnail"
    this.resumeKey = fingerprint(file, kind);
    this.sessionKey = null;
    this.partSize = null;
    this.totalParts = null;
    this.presignedByPart = new Map(); // part_number -> url
    this.completedByPart = new Map(); // part_number -> etag
    this.aborted = false;
    this.onProgress = null;
  }

  _emitProgress() {
    if (!this.onProgress) return;
    const done = this.completedByPart.size;
    this.onProgress({
      uploadedParts: done,
      totalParts: this.totalParts,
      progress: this.totalParts ? (done / this.totalParts) * 100 : 0,
    });
  }

  /**
   * Open a new upload, or re-attach to one this browser started earlier for
   * the same file. Returns true when an existing session was resumed.
   */
  async initialize() {
    const saved = readResume(this.resumeKey);
    if (saved) {
      try {
        const { data } = await api.get("/mrss/s3-upload/status/", {
          params: { session_key: saved.session_key },
        });
        if (data.status === "in_progress") {
          this.sessionKey = data.session_key;
          this.partSize = data.part_size;
          this.totalParts = data.total_parts;
          (data.uploaded_parts || []).forEach((p) =>
            this.completedByPart.set(p.part_number, p.etag)
          );
          this._emitProgress();
          return true;
        }
        if (data.status === "completed") {
          // Finished previously but never claimed (upload completed, then the
          // tab closed before submitting). The object is already staged on
          // S3 — reuse its key rather than re-sending the whole file.
          this.sessionKey = data.session_key;
          this.partSize = data.part_size;
          this.totalParts = data.total_parts;
          this.stagedKey = data.key || null;
          this.stagedBucket = data.bucket || null;
          this._alreadyComplete = true;
          return true;
        }
      } catch {
        // Session gone/expired on the server — fall through and start fresh.
      }
      clearResume(this.resumeKey);
    }

    const { data } = await api.post("/mrss/s3-upload/init/", {
      filename: this.file.name,
      filesize: this.file.size,
      content_type: this.file.type || undefined,
      kind: this.kind,
    });
    this.sessionKey = data.session_key;
    this.partSize = data.part_size;
    this.totalParts = data.total_parts;
    (data.parts || []).forEach((p) => this.presignedByPart.set(p.part_number, p.url));
    writeResume(this.resumeKey, { session_key: this.sessionKey });
    return false;
  }

  async _urlForPart(partNumber) {
    if (this.presignedByPart.has(partNumber)) return this.presignedByPart.get(partNumber);
    const { data } = await api.post("/mrss/s3-upload/presign/", {
      session_key: this.sessionKey,
      part_numbers: [partNumber],
    });
    const url = data.parts?.[0]?.url;
    if (!url) throw new Error(`Could not get an upload URL for part ${partNumber}`);
    this.presignedByPart.set(partNumber, url);
    return url;
  }

  async _uploadPart(partNumber) {
    if (this.aborted) throw new Error("Upload aborted");

    const start = (partNumber - 1) * this.partSize;
    const blob = this.file.slice(start, Math.min(start + this.partSize, this.file.size));

    let lastError;
    for (let attempt = 0; attempt < PART_MAX_RETRIES; attempt++) {
      try {
        const url = await this._urlForPart(partNumber);
        // Plain axios, not the app's `api` instance: this goes straight to
        // S3, and the app's auth headers/baseURL must not be attached (an
        // Authorization header would make S3 reject the presigned request).
        //
        // Content-Type is explicitly REMOVED (null strips it in axios v1)
        // rather than set. upload_part takes no ContentType parameter, so
        // the presigned URL cannot sign one — and any Content-Type we send
        // that the signature didn't cover is a guaranteed
        // SignatureDoesNotMatch under SigV2, which folds Content-Type into
        // its signed string. The finished object's type comes from
        // create_multipart_upload instead, where it IS signed.
        const resp = await axios.put(url, blob, {
          headers: { "Content-Type": null },
          timeout: 0, // a large part on a slow line can legitimately take a while
        });
        const etag = resp.headers?.etag || resp.headers?.ETag;
        if (!etag) throw new Error(`S3 did not return an ETag for part ${partNumber}`);
        this.completedByPart.set(partNumber, etag);
        this._emitProgress();
        return;
      } catch (err) {
        if (this.aborted) throw err;
        lastError = err;
        // A rejected presigned URL is usually just an expired one — drop it
        // so the next attempt asks for a fresh signature.
        this.presignedByPart.delete(partNumber);
        if (attempt < PART_MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, PART_RETRY_BASE_DELAY_MS * 2 ** attempt));
        }
      }
    }
    throw lastError;
  }

  /** Sliding-window worker pool — always keeps `concurrency` parts in flight. */
  async _uploadAllParts(concurrency) {
    const queue = [];
    for (let n = 1; n <= this.totalParts; n++) {
      if (!this.completedByPart.has(n)) queue.push(n);
    }
    this._emitProgress();

    let cursor = 0;
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (cursor < queue.length) {
        if (this.aborted) throw new Error("Upload aborted");
        await this._uploadPart(queue[cursor++]);
      }
    });
    await Promise.all(workers);
  }

  async complete() {
    const parts = Array.from(this.completedByPart.entries())
      .map(([part_number, etag]) => ({ part_number, etag }))
      .sort((a, b) => a.part_number - b.part_number);

    const { data } = await api.post("/mrss/s3-upload/complete/", {
      session_key: this.sessionKey,
      parts,
    });
    clearResume(this.resumeKey);
    return data; // { staged_key, staged_bucket }
  }

  async abort() {
    this.aborted = true;
    if (!this.sessionKey) return;
    try {
      await api.post("/mrss/s3-upload/abort/", { session_key: this.sessionKey });
    } catch {
      /* best effort — the server sweep and the bucket lifecycle rule also clean up */
    }
    clearResume(this.resumeKey);
  }

  /** Whether initialize() re-attached to a session that had already finished. */
  get alreadyComplete() {
    return Boolean(this._alreadyComplete);
  }

  /**
   * The parts phase on its own — for callers that drive initialize() and
   * complete() themselves because they need to react in between (e.g. to
   * report that an interrupted upload was resumed).
   */
  async uploadParts(concurrency = DEFAULT_CONCURRENCY) {
    return await this._uploadAllParts(concurrency);
  }

  async upload(concurrency = DEFAULT_CONCURRENCY) {
    await this.initialize();
    if (this._alreadyComplete) {
      clearResume(this.resumeKey);
      return { staged_key: this.stagedKey, staged_bucket: this.stagedBucket, resumed_complete: true };
    }
    await this._uploadAllParts(concurrency);
    return await this.complete();
  }
}

/**
 * Upload a File straight to S3's private staging area.
 *
 * Returns { staged_key, staged_bucket } — hand staged_key to the push
 * endpoint as staged_video_key/staged_thumb_key. Nothing is publicly
 * readable until an admin approves the push and the server promotes it.
 */
export const uploadFileToS3 = async (file, kind = "video", callbacks = {}) => {
  const uploader = new S3Uploader(file, kind);
  if (callbacks.onProgress) uploader.onProgress = callbacks.onProgress;
  return await uploader.upload(callbacks.concurrency || DEFAULT_CONCURRENCY);
};

export default S3Uploader;
